import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import * as broadcastsApi from '../api/broadcastsApi';
import { getUsers } from '../api/usersApi';
import {
  AddCreditsResult,
  addCredits,
  creditTemplateParams,
  FilterPreview,
  getWalletHolders,
  getWalletSummary,
  getWalletUser,
  previewFilter,
  renderTemplateBody,
  WalletHolder,
  WalletSummary,
  WalletTransaction,
  WalletUserDetail,
} from '../api/walletApi';
import { MetaTemplate } from '../types/broadcast';
import { AppUser } from '../types/user';
import {
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  Pagination,
  Skeleton,
  Spinner,
  TableSkeleton,
} from '../components/ui';
import { IconRefresh, IconSearch, IconX } from '../components/icons';
import { formatDate, formatDateTime, formatINR, shortId } from '../utils/format';

/**
 * Store credits.
 *
 * Two things happen on this page and they pull in opposite directions: issuing
 * credit is fast and irreversible, and reading the book is slow and harmless. So
 * the issuing half is deliberately obstructed — a filter has to be previewed
 * before it can be used, the total is always spelled out in rupees, and a
 * confirmation dialog restates the whole thing before anything is written.
 */

// ── Template parsing ─────────────────────────────────────────────────────────
// Same shape the broadcast builder reads; a template's copy lives on Meta, not
// in this app, so the preview is built from what the Graph API returned.

function bodyText(t: MetaTemplate | undefined): string {
  return t?.components?.find((c) => c.type?.toUpperCase() === 'BODY')?.text ?? '';
}

/** Highest {{n}} in a body — how many parameters Meta will expect. */
function expectedParamCount(text: string): number {
  let max = 0;
  const re = /\{\{(\d+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) max = Math.max(max, Number(m[1]));
  return max;
}

// ── Summary cards ────────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-sub">{label}</p>
      <p className="mt-2 text-2xl font-semibold">{value}</p>
      {sub && <p className="mt-1 text-xs text-sub">{sub}</p>}
    </div>
  );
}

// ── Transaction row ──────────────────────────────────────────────────────────

const TXN_LABEL: Record<string, string> = {
  credit_added: 'Added',
  credit_used: 'Used',
  credit_expired: 'Expired',
  credit_refunded: 'Refunded',
};

function txnTone(type: string): string {
  if (type === 'credit_used') return 'text-bad';
  if (type === 'credit_expired') return 'text-sub';
  return 'text-ok';
}

function TransactionRow({ txn }: { txn: WalletTransaction }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-edge py-3 last:border-0">
      <div className="min-w-0">
        <p className="text-sm font-medium">
          {txn.reference || TXN_LABEL[txn.type] || txn.type}
        </p>
        <p className="mt-0.5 text-xs text-sub">
          {TXN_LABEL[txn.type] ?? txn.type}
          {txn.order_id && <> · order {shortId(txn.order_id)}</>}
          {txn.expires_at && txn.type !== 'credit_expired' && (
            <> · expires {formatDate(txn.expires_at)}</>
          )}
          {txn.created_by && <> · by {shortId(txn.created_by)}</>}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className={`text-sm font-semibold ${txnTone(txn.type)}`}>
          {txn.amount < 0 ? '−' : '+'}
          {formatINR(Math.abs(txn.amount))}
        </p>
        <p className="mt-0.5 text-xs text-sub">{formatDate(txn.created_at)}</p>
      </div>
    </div>
  );
}

// ── Customer ledger slide-over ───────────────────────────────────────────────

function LedgerPanel({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [detail, setDetail] = useState<WalletUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getWalletUser(userId)
      .then((d) => alive && setDetail(d))
      .catch((err) => alive && setError(apiErrorMessage(err)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [userId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Customer credit history"
        className="relative z-10 flex h-full w-full max-w-md flex-col border-l border-edge bg-surface"
      >
        <div className="flex items-start justify-between gap-3 border-b border-edge px-5 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold">
              {detail?.user.display_name || 'Customer'}
            </h3>
            <p className="truncate text-xs text-sub">{detail?.user.email || shortId(userId)}</p>
          </div>
          <button onClick={onClose} className="btn-ghost !p-1.5" aria-label="Close">
            <IconX className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {error && <ErrorBanner message={error} />}
          {loading && !detail ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : detail ? (
            <>
              <div className="rounded-xl border border-edge p-4">
                <p className="text-xs uppercase tracking-wide text-sub">Available balance</p>
                <p className="mt-1 text-2xl font-semibold text-gold">
                  {formatINR(detail.balance)}
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-sub">
                  <span>Earned {formatINR(detail.lifetime_earned)}</span>
                  <span>Used {formatINR(detail.lifetime_used)}</span>
                  <span>Expired {formatINR(detail.lifetime_expired)}</span>
                </div>
                {detail.expires_soonest_at && (
                  <p className="mt-2 text-xs text-warn">
                    Next expiry {formatDate(detail.expires_soonest_at)}
                  </p>
                )}
              </div>

              <p className="mb-1 mt-5 text-xs font-medium uppercase tracking-wide text-sub">
                Ledger
              </p>
              {detail.transactions.length === 0 ? (
                <EmptyState message="No transactions" />
              ) : (
                detail.transactions.map((t) => <TransactionRow key={t.id} txn={t} />)
              )}
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

// ── Add credits panel ────────────────────────────────────────────────────────

type Mode = 'users' | 'filter';

function AddCreditsPanel({ onDone }: { onDone: () => void }) {
  const [mode, setMode] = useState<Mode>('users');

  // Specific-users mode. Search reuses the existing admin users endpoint —
  // there is no second directory to build.
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AppUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<AppUser[]>([]);

  // Filter mode.
  const [days, setDays] = useState('30');
  const [onlyOrdered, setOnlyOrdered] = useState(false);
  const [minOrders, setMinOrders] = useState('1');
  const [preview, setPreview] = useState<FilterPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);

  // Common fields.
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [noExpiry, setNoExpiry] = useState(true);
  const [expiryDate, setExpiryDate] = useState('');

  // Notifications.
  const [notify, setNotify] = useState(false);
  const [templates, setTemplates] = useState<MetaTemplate[]>([]);
  const [templatesError, setTemplatesError] = useState('');
  const [templateName, setTemplateName] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<AddCreditsResult | null>(null);

  // Debounced user search — typing a name should not fire a request per key.
  useEffect(() => {
    if (mode !== 'users' || query.trim().length < 2) {
      setResults([]);
      return;
    }
    let alive = true;
    setSearching(true);
    const timer = setTimeout(() => {
      getUsers({ search: query.trim(), limit: 8 })
        .then((r) => alive && setResults(r.items))
        .catch(() => alive && setResults([]))
        .finally(() => alive && setSearching(false));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query, mode]);

  // Templates load only when notifications are switched on — most campaigns
  // don't notify, and this is a live Graph API call.
  useEffect(() => {
    if (!notify || templates.length > 0) return;
    broadcastsApi
      .getTemplates()
      .then(setTemplates)
      .catch((err) => setTemplatesError(apiErrorMessage(err)));
  }, [notify, templates.length]);

  // Any change to the filter invalidates its preview: a stale count is worse
  // than no count, because it is the number the admin is about to act on.
  useEffect(() => {
    setPreview(null);
  }, [days, onlyOrdered, minOrders, mode]);

  const amountValue = Number(amount);
  const expiresAt = noExpiry || !expiryDate ? null : new Date(`${expiryDate}T23:59:59Z`).toISOString();

  const audienceFilter = useMemo(
    () => ({
      ...(days.trim() ? { days: Number(days) } : {}),
      ...(onlyOrdered ? { has_ordered: true, min_orders: Number(minOrders) || 1 } : {}),
    }),
    [days, onlyOrdered, minOrders],
  );

  // How many people this will credit. In users mode it's the selection; in
  // filter mode it is UNKNOWN until previewed — deliberately, so nobody commits
  // to a number they have not seen.
  const userCount = mode === 'users' ? selected.length : preview?.user_count ?? null;
  const withoutPhone = mode === 'users' ? null : preview?.users_without_phone ?? null;
  const totalIssue = userCount !== null && amountValue > 0 ? userCount * amountValue : null;

  const chosenTemplate = templates.find((t) => t.name === templateName);
  const templateBody = bodyText(chosenTemplate);
  const templateParams = creditTemplateParams({
    displayName: null,
    amount: amountValue > 0 ? amountValue : 0,
    expiresAt,
  });
  const paramMismatch =
    chosenTemplate !== undefined && expectedParamCount(templateBody) !== templateParams.length;

  const ready =
    amountValue > 0 &&
    reference.trim().length > 0 &&
    (noExpiry || expiryDate.length > 0) &&
    userCount !== null &&
    userCount > 0 &&
    (!notify || (templateName.length > 0 && !paramMismatch));

  async function runPreview() {
    setPreviewing(true);
    setError('');
    try {
      setPreview(await previewFilter(audienceFilter));
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  }

  async function commit() {
    setBusy(true);
    setError('');
    try {
      const res = await addCredits({
        ...(mode === 'users'
          ? { user_ids: selected.map((u) => u.id) }
          : { filter: audienceFilter }),
        amount: amountValue,
        reference: reference.trim(),
        expires_at: expiresAt,
        send_notification: notify,
        notification_template: notify ? templateName : null,
      });
      setResult(res);
      setConfirmOpen(false);
      // Reset the audience but keep the campaign fields: crediting a second
      // group under the same campaign name is the common follow-up.
      setSelected([]);
      setPreview(null);
      onDone();
    } catch (err) {
      setError(apiErrorMessage(err));
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Add credits">
      {result && (
        <div className="mb-5 rounded-xl border border-ok/40 bg-ok/10 px-4 py-3 text-sm text-ok">
          <p className="font-semibold">
            ✓ {formatINR(result.total_credits_issued / Math.max(1, result.users_credited))} credited
            to {result.users_credited} user{result.users_credited === 1 ? '' : 's'}.
          </p>
          <p className="mt-1 text-xs">
            {result.notifications_sent} WhatsApp notification
            {result.notifications_sent === 1 ? '' : 's'} sent.{' '}
            {result.users_without_phone > 0 &&
              `${result.users_without_phone} skipped (no phone number).`}
          </p>
        </div>
      )}

      {error && <div className="mb-4"><ErrorBanner message={error} /></div>}

      {/* Mode switch */}
      <div className="mb-5 inline-flex rounded-xl border border-edge p-1">
        {(['users', 'filter'] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={
              mode === m
                ? 'rounded-lg bg-gold px-3 py-1.5 text-xs font-semibold text-[#1a1408]'
                : 'rounded-lg px-3 py-1.5 text-xs text-sub hover:text-ink'
            }
          >
            {m === 'users' ? 'Specific Users' : 'Filter-based'}
          </button>
        ))}
      </div>

      {mode === 'users' ? (
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs text-sub">Search by name or email</span>
            <div className="relative">
              <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sub" />
              <input
                className="input !pl-9"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Start typing…"
              />
            </div>
          </label>

          {searching && <p className="text-xs text-sub">Searching…</p>}

          {results.length > 0 && (
            <div className="divide-y divide-edge rounded-xl border border-edge">
              {results.map((u) => {
                const already = selected.some((s) => s.id === u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    disabled={already}
                    onClick={() => {
                      setSelected((prev) => [...prev, u]);
                      setQuery('');
                    }}
                    className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-edge/30 disabled:opacity-40"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{u.name || 'Unnamed'}</span>
                      <span className="block truncate text-xs text-sub">{u.email}</span>
                    </span>
                    <span className="shrink-0 text-xs text-sub">{already ? 'Added' : 'Add'}</span>
                  </button>
                );
              })}
            </div>
          )}

          {selected.length > 0 && (
            <div>
              <p className="mb-2 text-xs text-sub">{selected.length} users selected</p>
              <div className="flex flex-wrap gap-2">
                {selected.map((u) => (
                  <span
                    key={u.id}
                    className="inline-flex items-center gap-2 rounded-lg border border-edge px-2.5 py-1 text-xs"
                  >
                    {u.name || u.email}
                    <button
                      type="button"
                      onClick={() => setSelected((prev) => prev.filter((s) => s.id !== u.id))}
                      aria-label={`Remove ${u.name || u.email}`}
                      className="text-sub hover:text-bad"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <label className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-sub">Users active in last</span>
            <input
              type="number"
              min={1}
              className="input !w-24"
              value={days}
              onChange={(e) => setDays(e.target.value)}
            />
            <span className="text-sub">days</span>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={onlyOrdered}
              onChange={(e) => setOnlyOrdered(e.target.checked)}
              className="h-4 w-4 accent-[#c9a96e]"
            />
            Only users who have placed an order
          </label>

          {onlyOrdered && (
            <label className="flex flex-wrap items-center gap-2 pl-6 text-sm">
              <span className="text-sub">Minimum orders</span>
              <input
                type="number"
                min={1}
                className="input !w-24"
                value={minOrders}
                onChange={(e) => setMinOrders(e.target.value)}
              />
            </label>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className="btn-outline" onClick={runPreview} disabled={previewing}>
              {previewing && <Spinner />}
              Preview
            </button>
            {preview && (
              <p className="text-sm">
                This will credit <strong>{preview.user_count}</strong> user
                {preview.user_count === 1 ? '' : 's'}
                {preview.users_without_phone > 0 && (
                  <span className="text-sub">
                    {' '}
                    · {preview.users_without_phone} without a phone number
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Common fields */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs text-sub">Amount per user (₹)</span>
          <input
            type="number"
            min={1}
            className="input"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="500"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs text-sub">Campaign name</span>
          <input
            className="input"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Diwali 2026 Reward"
          />
        </label>

        <div>
          <span className="mb-1 block text-xs text-sub">Expiry date</span>
          <input
            type="date"
            className="input"
            value={expiryDate}
            disabled={noExpiry}
            onChange={(e) => setExpiryDate(e.target.value)}
          />
          <label className="mt-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={noExpiry}
              onChange={(e) => setNoExpiry(e.target.checked)}
              className="h-4 w-4 accent-[#c9a96e]"
            />
            No expiry
          </label>
        </div>
      </div>

      {/* Notifications */}
      <div className="mt-6 border-t border-edge pt-5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            className="h-4 w-4 accent-[#c9a96e]"
          />
          Send WhatsApp notification to users
        </label>

        {notify && (
          <div className="mt-3 space-y-3">
            {templatesError && <ErrorBanner message={templatesError} />}

            <label className="block">
              <span className="mb-1 block text-xs text-sub">Template (approved only)</span>
              <select
                className="input"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              >
                <option value="">Select a template…</option>
                {templates
                  .filter((t) => t.status?.toUpperCase() === 'APPROVED')
                  .map((t) => (
                    <option key={`${t.name}:${t.language}`} value={t.name}>
                      {t.name} ({t.language} · {t.category})
                    </option>
                  ))}
              </select>
            </label>

            {chosenTemplate && (
              <div className="rounded-xl border border-edge bg-edge/20 p-4">
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-sub">
                  Message preview
                </p>
                <p className="whitespace-pre-wrap text-sm">
                  {renderTemplateBody(templateBody, templateParams)}
                </p>
                {paramMismatch && (
                  <p className="mt-2 text-xs text-bad">
                    This template expects {expectedParamCount(templateBody)} parameter
                    {expectedParamCount(templateBody) === 1 ? '' : 's'}, but a credit notification
                    sends {templateParams.length} (name, amount, expiry). Meta would reject it —
                    pick a template with three variables.
                  </p>
                )}
              </div>
            )}

            {withoutPhone !== null && withoutPhone > 0 && (
              <p className="text-xs text-warn">
                Users without a phone number ({withoutPhone} users) will not receive notifications.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Commit */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-edge pt-5">
        <p className="text-sm">
          {totalIssue !== null ? (
            <>
              Total credits to issue: <strong className="text-gold">{formatINR(totalIssue)}</strong>
            </>
          ) : (
            <span className="text-sub">
              {mode === 'filter' && userCount === null
                ? 'Preview the filter to see how many users it selects.'
                : 'Enter an amount and pick an audience.'}
            </span>
          )}
        </p>
        <button className="btn-gold" disabled={!ready} onClick={() => setConfirmOpen(true)}>
          Add Credits →
        </button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Add store credits"
        confirmLabel="Confirm"
        busy={busy}
        onClose={() => setConfirmOpen(false)}
        onConfirm={commit}
        message={
          <div className="space-y-2">
            <p>
              Add <strong>{formatINR(amountValue)}</strong> credits to <strong>{userCount}</strong>{' '}
              user{userCount === 1 ? '' : 's'} ({formatINR(totalIssue ?? 0)} total)?
            </p>
            <p>{expiresAt ? `Expires ${formatDate(expiresAt)}` : 'These credits never expire.'}</p>
            {notify && (
              <p>
                WhatsApp will be sent to{' '}
                {withoutPhone !== null && userCount !== null
                  ? userCount - withoutPhone
                  : userCount}{' '}
                users using <code>{templateName}</code>.
              </p>
            )}
            <p className="font-semibold text-bad">This cannot be undone.</p>
          </div>
        }
      />
    </Card>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function WalletPage() {
  const [summary, setSummary] = useState<WalletSummary | null>(null);
  const [holders, setHolders] = useState<WalletHolder[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expiringSoonDays, setExpiringSoonDays] = useState(7);
  const [page, setPage] = useState(1);
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openUser, setOpenUser] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s, h] = await Promise.all([
        getWalletSummary(),
        getWalletHolders({ page, has_expiry_soon: expiringOnly }),
      ]);
      setSummary(s);
      setHolders(h.users);
      setTotalPages(h.totalPages);
      setTotal(h.total);
      setExpiringSoonDays(h.expiringSoonDays);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, expiringOnly]);

  useEffect(() => {
    load();
  }, [load]);

  /** Whether a balance has something lapsing inside the server's soon-window. */
  const isExpiringSoon = useCallback(
    (at: string | null) => {
      if (!at) return false;
      return new Date(at).getTime() - Date.now() <= expiringSoonDays * 86_400_000;
    },
    [expiringSoonDays],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Wallet</h1>
          <p className="text-xs text-sub">
            Store credits granted as rewards. Customers spend them at checkout — they can never
            top up.
          </p>
        </div>
        <button className="btn-outline" onClick={load} disabled={loading}>
          <IconRefresh className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      {loading && !summary ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            label="Total outstanding credits"
            value={formatINR(summary.total_credits_outstanding)}
            sub={`${summary.users_with_balance} customers · ${formatINR(
              summary.avg_balance_per_user,
            )} avg`}
          />
          <StatCard
            label="Credits issued (all time)"
            value={formatINR(summary.total_credits_issued_lifetime)}
          />
          <StatCard
            label="Credits redeemed"
            value={formatINR(summary.total_credits_redeemed_lifetime)}
          />
          <StatCard
            label="Credits expired"
            value={formatINR(summary.total_credits_expired_lifetime)}
          />
        </div>
      ) : null}

      <AddCreditsPanel onDone={load} />

      <Card
        title={`Customers with a balance${total ? ` (${total})` : ''}`}
        bodyClassName=""
        action={
          <label className="flex items-center gap-2 text-xs text-sub">
            <input
              type="checkbox"
              checked={expiringOnly}
              onChange={(e) => {
                setPage(1);
                setExpiringOnly(e.target.checked);
              }}
              className="h-4 w-4 accent-[#c9a96e]"
            />
            Expiring within {expiringSoonDays} days
          </label>
        }
      >
        {loading && holders.length === 0 ? (
          <TableSkeleton />
        ) : holders.length === 0 ? (
          <EmptyState
            message="Nobody is holding credits"
            hint="Credits granted above will appear here."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-edge text-left text-xs uppercase tracking-wide text-sub">
                    <th className="px-5 py-3 font-medium">User</th>
                    <th className="px-5 py-3 font-medium">Balance</th>
                    <th className="px-5 py-3 font-medium">Expiring soon</th>
                    <th className="px-5 py-3 font-medium">Last transaction</th>
                  </tr>
                </thead>
                <tbody>
                  {holders.map((h) => (
                    <tr
                      key={h.user_id}
                      onClick={() => setOpenUser(h.user_id)}
                      className="cursor-pointer border-b border-edge transition-colors last:border-0 hover:bg-edge/20"
                    >
                      <td className="px-5 py-3">
                        <p className="font-medium">{h.display_name || 'Unnamed'}</p>
                        <p className="text-xs text-sub">{h.email || shortId(h.user_id)}</p>
                      </td>
                      <td className="px-5 py-3 font-semibold text-gold">{formatINR(h.balance)}</td>
                      <td className="px-5 py-3">
                        {h.expires_soonest_at ? (
                          <span className={isExpiringSoon(h.expires_soonest_at) ? 'text-warn' : ''}>
                            {formatDate(h.expires_soonest_at)}
                          </span>
                        ) : (
                          <span className="text-sub">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-sub">
                        {h.last_transaction_at ? formatDateTime(h.last_transaction_at) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onPage={setPage} />
          </>
        )}
      </Card>

      {openUser && <LedgerPanel userId={openUser} onClose={() => setOpenUser(null)} />}
    </div>
  );
}
